/**
 * Simple, zero-dependency cookie helper module.
 */
export default {
  /**
   * Get a cookie value by name.
   *
   * @param {string} name
   * @returns {string | null}
   */
  get(name) {
    const prefix = `${encodeURIComponent(name)}=`
    const match = document.cookie?.split(';').find((row) => row.trim().startsWith(prefix))

    return match ? decodeURIComponent(match.trim().slice(prefix.length)) : null
  },

  /**
   * Get the names of all cookies readable from JavaScript.
   *
   * @returns {string[]}
   */
  names() {
    return (document.cookie?.split(';') ?? [])
      .map((row) => row.trim())
      .filter((row) => row !== '')
      .map((row) => decodeURIComponent(row.split('=')[0]))
  },

  /**
   * Set a cookie.
   *
   * @param {string} name
   * @param {string} value
   * @param {Object} [options]
   * @param {number} [options.maxAge] - Lifetime in SECONDS
   * @param {string} [options.path='/']
   * @param {string} [options.domain]
   * @param {boolean} [options.secure] - Defaults to true on HTTPS
   * @param {'Lax'|'Strict'|'None'} [options.sameSite='Lax']
   * @param {boolean} [options.partitioned=false] - Opt-in to CHIPS
   */
  set(name, value, { maxAge, path = '/', domain, secure = location.protocol === 'https:', sameSite = 'Lax', partitioned = false } = {}) {
    let cookieStr = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

    if (maxAge !== undefined) {
      cookieStr += `; max-age=${maxAge}`
    }

    cookieStr += `; path=${path}`

    if (domain) {
      cookieStr += `; domain=${domain}`
    }

    if (secure) {
      cookieStr += '; secure'
    }

    if (partitioned) {
      cookieStr += '; partitioned'
    }

    cookieStr += `; samesite=${sameSite}`

    document.cookie = cookieStr
  },

  /**
   * Delete a cookie.
   *
   * @param {string} name
   * @param {Partial<{ path: string, domain: string, sameSite: 'Lax'|'Strict'|'None' }>} [options]
   */
  remove(name, options = {}) {
    this.set(name, '', { ...options, maxAge: 0 })
  },
}
